import React, { useRef, useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import logo from "./Userlayout/TOSC_white.png";

import { alpha, styled } from "@mui/material/styles";
import { TextField, Typography, Divider } from "@mui/material";
import { FilledInput } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import InputLabel from "@mui/material/InputLabel";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";

import GoogleIcon from "@mui/icons-material/Google";

import { Grid, Button, Card } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import LockResetIcon from "@mui/icons-material/LockReset";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CssTextField = styled(TextField)({
  "& .MuiInputBase-root.Mui-disabled": {
    position: "relative",
    backgroundColor: "#f0f0f0",
    color: "#000",
  },
  "& label.Mui-focused": {
    color: "grey",
    input: {
      color: "white",
    },
  },
  "& label.Mui": {
    color: "white",
    input: {
      color: "white",
    },
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "white",
    color: "white",
    input: {
      color: "white",
    },
  },
  "& .MuiFormLabel-root": {
    borderBottomColor: "white",
    color: "grey",
    input: {
      color: "white",
    },
  },
  "& .MuiOutlinedInput-root": {
    input: {
      color: "white",
    },
    "& fieldset": {
      borderColor: "grey",
      color: "white",
    },
    "&:hover fieldset": {
      borderColor: "grey",
      color: "white",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1976d2",
      color: "white",
      input: {
        color: "white",
      },
    },
    "&.MuiInputBase-input fieldset": {
      borderColor: "white",
      color: "white",
      zIndex: 3,
      input: {
        color: "white",
      },
    },
  },
});

// Styled Dialog with dark theme
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    background:
      "linear-gradient(135deg, rgba(25,25,35,0.95) 0%, rgba(15,15,25,0.98) 100%)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.36)",
    color: "white",
    minHeight: "300px",
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(45deg, #1976d2 30%, #21CBF3 90%)",
  border: 0,
  borderRadius: "8px",
  color: "white",
  height: 48,
  padding: "0 30px",
  boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
  "&:hover": {
    background: "linear-gradient(45deg, #1565c0 30%, #00B0FF 90%)",
    boxShadow: "0 4px 8px 2px rgba(33, 203, 243, .4)",
  },
  "&:disabled": {
    background: "rgba(255, 255, 255, 0.1)",
    color: "rgba(255, 255, 255, 0.3)",
    boxShadow: "none",
  },
}));

const OutlineButton = styled(Button)(({ theme }) => ({
  border: "1px solid rgba(255, 255, 255, 0.3)",
  borderRadius: "8px",
  color: "rgba(255, 255, 255, 0.8)",
  padding: "8px 24px",
  "&:hover": {
    border: "1px solid rgba(255, 255, 255, 0.5)",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
  },
}));

export default function Login() {
  const [open, setOpen] = React.useState(true);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState("");

  const emailRef = useRef();
  const passwordRef = useRef();
  const { login, currentUser, resetPassword } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check if user is already authenticated on component mount
  useEffect(() => {
    if (currentUser) {
      navigate("/dashboard", { replace: true });
    }
  }, [currentUser, navigate]);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleForgotPasswordOpen = () => {
    setForgotPasswordOpen(true);
    setForgotPasswordEmail("");
    setForgotPasswordMessage("");
    setForgotPasswordError("");
  };

  const handleForgotPasswordClose = () => {
    setForgotPasswordOpen(false);
  };

  async function handleForgotPasswordSubmit(e) {
    e.preventDefault();

    if (!forgotPasswordEmail) {
      setForgotPasswordError("Please enter your email address");
      return;
    }

    try {
      setForgotPasswordError("");
      setForgotPasswordMessage("");
      setForgotPasswordLoading(true);

      // Use Firebase password reset
      await resetPassword(forgotPasswordEmail);
      setForgotPasswordMessage(
        "Check your email for password reset instructions"
      );

      // Close the dialog after a short delay
      setTimeout(() => {
        setForgotPasswordOpen(false);
      }, 3000);
    } catch (err) {
      console.error("Error:", err.message);
      setForgotPasswordError(err.message || "Failed to send reset email");
    } finally {
      setForgotPasswordLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError("");
      setOpen(false);
      setLoading(true);

      // Use Firebase authentication
      await login(emailRef.current.value, passwordRef.current.value);
      // Ensure immediate redirection to dashboard
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Error:", err.message);
      setError(err.message || "Failed to sign in");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }

  // If user is already authenticated, show loading or redirect immediately
  if (currentUser) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      className="login-gl"
      sx={{
        backgroundImage: `url('your-background-image-path')`,
        backgroundSize: "cover",
        height: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        className="dashboard-card"
        sx={{
          padding: 4,
          width: 400,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            color: "white",
            textAlign: "center",
            borderRadius: "8px 8px 8px 8px",
            fontSize: "1.5rem",
          }}
        >
          <a href="https://tosc.techkriti.org">
            {" "}
            <img src={logo} alt="TOSC logo" style={{ width: "300px" }} />
          </a>

          <h2 className="text-center mb-4">Sign in</h2>
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={handleClose}
              >
                <Alert onClose={handleClose} severity="error">
                  {error}
                </Alert>
              </Snackbar>
            )}
            <Form.Group id="email" className="login-input">
              <CssTextField
                label="Email"
                type="email"
                inputRef={emailRef}
                fullWidth
                sx={{ mb: 2, zIndex: 3 }}
              />
            </Form.Group>

            <Form.Group id="password" className="login-input">
              <CssTextField
                label="Password"
                type="password"
                inputRef={passwordRef}
                fullWidth
                sx={{ mb: 2, zIndex: 3 }}
              />
            </Form.Group>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              type="submit"
              disabled={loading}
              sx={{
                mb: 2,
              }}
            >
              Sign in
            </Button>

            <Box textAlign="center" sx={{ paddingTop: 2 }}>
              Forgot your password? Reset it{" "}
              <Link
                to="#"
                onClick={handleForgotPasswordOpen}
                style={{
                  textDecoration: "none",
                  color: "#21CBF3",
                  cursor: "pointer",
                }}
              >
                here
              </Link>
            </Box>
            <Box textAlign="center" mt={2} sx={{ paddingTop: 2 }}>
              Haven't registered yet?{" "}
              <Link
                to="/signup"
                style={{ textDecoration: "none", color: "#21CBF3" }}
              >
                Register
              </Link>
            </Box>
          </form>
        )}
      </Box>

      {/* Forgot Password Dialog */}
      <StyledDialog
        open={forgotPasswordOpen}
        onClose={handleForgotPasswordClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            textAlign: "center",
            position: "relative",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <LockResetIcon
            sx={{
              fontSize: 48,
              color: "#21CBF3",
              mb: 2,
              background: "linear-gradient(45deg, #1976d2 30%, #21CBF3 90%)",
              borderRadius: "50%",
              p: 1,
            }}
          />
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: "bold",
              background: "linear-gradient(45deg, #fff 30%, #21CBF3 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            Reset Password
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleForgotPasswordClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "rgba(255, 255, 255, 0.7)",
              "&:hover": {
                color: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleForgotPasswordSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                textAlign: "center",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: 1.6,
              }}
            >
              Enter your email address and we'll send you instructions to reset
              your password.
            </Typography>

            {forgotPasswordError && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  background: "rgba(211, 47, 47, 0.9)",
                  color: "white",
                }}
              >
                {forgotPasswordError}
              </Alert>
            )}

            {forgotPasswordMessage && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  background: "rgba(46, 125, 50, 0.9)",
                  color: "white",
                }}
              >
                {forgotPasswordMessage}
              </Alert>
            )}

            <CssTextField
              label="Email Address"
              type="email"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              fullWidth
              disabled={forgotPasswordLoading}
              sx={{ mb: 2 }}
              InputProps={{
                sx: {
                  "& .MuiInputBase-input": {
                    color: "white",
                  },
                },
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              p: 3,
              gap: 2,
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <OutlineButton
              onClick={handleForgotPasswordClose}
              disabled={forgotPasswordLoading}
            >
              Cancel
            </OutlineButton>
            <GradientButton
              type="submit"
              disabled={forgotPasswordLoading}
              fullWidth
            >
              {forgotPasswordLoading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                "Send Reset Link"
              )}
            </GradientButton>
          </DialogActions>
        </form>
      </StyledDialog>
    </Box>
  );
}
